<template>
  <v-expansion-panels>
    <v-expansion-panel focusable class="mb-4">
      <v-expansion-panel-header class="panel-header">
        <div class="body-2">
          <span class="filter-text-title">{{ $t(titleLocalisation) }}:</span>
          <v-icon v-once class="filter-text" size="2.75rem">filter_list</v-icon>
          <span class="font-weight-bold filter-text">
            {{ $tc(filterCountLocalisation, numFiltersApplied) }}
          </span>
        </div>
        <template v-slot:actions>
          <v-icon v-once color="indigo darken-2">expand_more</v-icon>
        </template>
      </v-expansion-panel-header>
      <v-expansion-panel-content class="panel-header">
        <v-card-text class="px-6 pt-0">
          <!-- Filter boxes for each existing filter rule. -->
          <!-- independent filters -->
          <v-flex v-for="(rule, index) in mutableFilterRulesWithMetadata" :key="rule.key">
            <v-divider
              v-if="mutableFilterRulesWithMetadata.length > 1 && index > 0"
              :key="index"
              class="my-2"
            />
            <attribute-filter
              :key="rule.key"
              :ruleset-type="rulesetType"
              :filter-rule="rule.rule"
              :selected-attribute-metadata="rule.selectedAttributeMetadata"
              :available-attribute-metadata="availableAttributeMetadata"
              :use-reporting-store="useReportingStore"
              @update-filter-attributes="updateAttributeFilter($event, rule.key)"
              @remove-filter="removeAttributeFilter(rule.key)"
              @filter-blurred-with-value="emitAttributeFilterChange"
            />
          </v-flex>

          <!-- Button to add an additional filter rule -->
          <v-layout row>
            <v-btn right icon text depressed @click="addAttributeFilter">
              <v-icon v-once color="indigo darken-2" size="2.75rem">add_box</v-icon>
            </v-btn>
          </v-layout>
        </v-card-text>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import {
  cloneDeep,
  filter,
  pick,
  uniqueId,
  includes,
  find,
  size,
  isEmpty,
  slice,
  forEach,
  intersection,
  map,
} from 'lodash';
import {
  attributeOption,
  storegroupOption,
  chainOption,
  attributeDescriptionOption,
  clientProductKeyOption,
  similarityGroupOption,
  cannGroupOption,
} from '@enums/filter-options';
import hierarchyFiltersMixin from '../mixins/hierarchyFilters';
import productAttributesFiltersMixin from '../mixins/productAttributesFilters';
import { architectureGroupLevel, rootHierarchyLevel } from '@enums/hierarchy';
import {
  useZones,
  useStoreGroupChains,
  displayClientProductKey,
  enableSimilarityGroups,
} from '@enums/feature-flags';
import featureFlagsMixin from '../mixins/featureFlags';

export default {
  mixins: [hierarchyFiltersMixin, productAttributesFiltersMixin, featureFlagsMixin],

  props: {
    rulesetType: {
      type: String,
      required: false,
      default: '',
    },
    filterRules: {
      type: Array,
      required: false,
      default: () => [],
    },
    // e.g. for line pricing, products are always filtered by AG
    fixedFilterRules: {
      type: Array,
      required: false,
      default: () => [],
    },
    // When true, uses reporting store for intersected attributes/hierarchy across multiple workpackages
    useReportingStore: {
      type: Boolean,
      required: false,
      default: false,
    },
    // name of filter title in localisation file
    titleLocalisation: {
      type: String,
      required: true,
    },
    // name of filter count in localisation file
    filterCountLocalisation: {
      type: String,
      required: true,
    },
    enableHierarchyFilters: {
      type: Boolean,
      default: false,
    },
    hierarchyFilterLevel: {
      type: Number,
      default: rootHierarchyLevel,
    },
    enableWholesaleHierarchyFilters: {
      type: Boolean,
      default: false,
    },
    enableNonAttributeFilters: {
      type: Boolean,
      default: true,
    },
    enableAttributeFilters: {
      type: Boolean,
      default: true,
    },
    enableAttributeDescriptionFilter: {
      type: Boolean,
      default: false,
    },
    enableToolStoreGroupFilter: {
      type: Boolean,
      default: true,
    },
    enableStoreGroupChainFilter: {
      type: Boolean,
      default: true,
    },
    enableClientProductKeyFilter: {
      type: Boolean,
      default: true,
    },
    enableSimilarityGroupFilter: {
      type: Boolean,
      default: true,
    },
    enableCannGroupFilter: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      mutableFilterRules: this.filterRules.length
        ? cloneDeep(this.filterRules).map(rule => ({
            key: uniqueId('filter-rule-'), // note this may have collisions with large number of rules. see https://ahtdao.atlassian.net/browse/AHTDPR-1189
            rule,
          }))
        : [this.filterRule()],
      nonEmptyRules: [],
      isNewTab: false,
    };
  },

  events: {
    onArchitectureRulesFilterUpdated(newFilterValue) {
      // don't carry selected attribute filters to changed architecture group
      // only do this on LPG, i.e. where hierarchy filters not enabled
      if (!this.enableHierarchyFilters && !newFilterValue) {
        this.mutableFilterRules = [this.filterRule()];
      }

      // If new filter value is explicitly passed (from architecture-group-filter.vue), then use it
      if (newFilterValue)
        this.mutableFilterRules = [{ ...this.filterRule(), rule: newFilterValue }];
    },
  },

  computed: {
    ...mapState('attributes', ['attributeMetadata', 'availableAttributeValues']),
    ...mapState('reporting', ['availableAttributes', 'availableHierarchyFilters']),
    ...mapGetters('filters', ['getSelectedFilter']),

    // Returns attribute metadata from the appropriate store
    activeAttributeMetadata() {
      return this.useReportingStore ? this.availableAttributes : this.attributeMetadata;
    },

    // Returns hierarchy filters from the appropriate store
    activeHierarchyFilters() {
      if (this.useReportingStore) {
        return this.availableHierarchyFilters;
      }

      // In non-reporting mode, use the same logic as availableAttributeMetadata
      if (this.enableHierarchyFilters) {
        return this.getHierarchyFilters({ level: this.hierarchyFilterLevel });
      }

      if (this.enableWholesaleHierarchyFilters) {
        return this.getWholesaleHierarchyFilters();
      }

      return [];
    },

    numFiltersApplied() {
      return isEmpty(this.filterRules[0]) ? 0 : this.filterRules.length;
    },

    appliedRules() {
      return filter(this.nonEmptyRules, rule => size(rule.attributeValue));
    },

    mutableFilterRulesWithMetadata() {
      return this.mutableFilterRules.map((rule, index) => ({
        ...rule,
        selectedAttributeMetadata: this.extractRuleMetadata(
          this.mutableFilterRules.slice(0, index)
        ),
      }));
    },

    availableAttributeMetadata() {
      // in the UI, when a user selects an attribute filter it's removed from the other dropdowns.
      // here we need to keep the newTabRule, so that architectureGroup is selected in the first dropdown.

      let attributeFilters = [];

      if (
        this.enableAttributeFilters &&
        this.activeAttributeMetadata &&
        this.activeAttributeMetadata.length > 0
      ) {
        attributeFilters = this.activeAttributeMetadata.map(attribute => ({
          ...attribute,
          attributeKey: attribute.attributeKey || attribute.key,
          attributeFilterType: attributeOption,
        }));
      }

      if (this.enableNonAttributeFilters) {
        const productAttributesFilters = this.getProductAttributesFilters();
        attributeFilters = [...attributeFilters, ...productAttributesFilters];
      }

      // Add hierarchy filters if enabled (activeHierarchyFilters handles both reporting and normal mode)
      if (this.enableHierarchyFilters || this.enableWholesaleHierarchyFilters) {
        attributeFilters = [...attributeFilters, ...this.activeHierarchyFilters];
      }

      if (this.enableAttributeDescriptionFilter) {
        attributeFilters.push({
          attributeKey: 'attributeKey',
          attributeDescription: this.$t('pricing.splitBy'),
          displayDescription: this.$t('pricing.splitBy'),
          attributeFilterType: attributeDescriptionOption,
        });
      }

      if (this.isFeatureFlagEnabled(useZones) && this.enableToolStoreGroupFilter) {
        attributeFilters.push({
          attributeKey: 'toolStoreGroupKey',
          attributeDescription: this.$t('pricing.toolStoreGroup'),
          displayDescription: this.$t('pricing.toolStoreGroup'),
          attributeFilterType: storegroupOption,
        });
        if (this.isFeatureFlagEnabled(useStoreGroupChains) && this.enableStoreGroupChainFilter) {
          attributeFilters.push({
            attributeKey: 'storeGroupChain',
            attributeDescription: this.$t('pricing.storeGroupChain'),
            displayDescription: this.$t('pricing.storeGroupChain'),
            attributeFilterType: chainOption,
          });
        }
      }

      if (this.isFeatureFlagEnabled(displayClientProductKey) && this.enableClientProductKeyFilter) {
        attributeFilters.push({
          attributeKey: 'clientProductKey',
          attributeDescription: this.$t('pricing.clientProductKey'),
          displayDescription: this.$t('pricing.clientProductKey'),
          attributeFilterType: clientProductKeyOption,
        });
      }

      if (this.isFeatureFlagEnabled(enableSimilarityGroups) && this.enableSimilarityGroupFilter) {
        attributeFilters.push({
          attributeKey: 'similarityGroup',
          attributeDescription: this.$t('pricing.similarityGroup'),
          displayDescription: this.$t('pricing.similarityGroup'),
          attributeFilterType: similarityGroupOption,
        });
      }

      if (this.isFeatureFlagEnabled(enableSimilarityGroups) && this.enableCannGroupFilter) {
        attributeFilters.push({
          attributeKey: 'cannGroup',
          attributeDescription: this.$t('pricing.cannGroup'),
          displayDescription: this.$t('pricing.cannGroup'),
          attributeFilterType: cannGroupOption,
        });
      }

      return attributeFilters;
    },
  },

  watch: {
    filterRules: {
      handler(newFilterRules, oldFilterRules) {
        if (oldFilterRules === undefined) return;

        // If all rules have been removed, add an empty rule.
        if (isEmpty(newFilterRules)) {
          this.mutableFilterRules = [this.filterRule()];
        } else {
          this.mutableFilterRules = cloneDeep(newFilterRules).map(rule => ({
            key: uniqueId('filter-rule-'),
            rule,
          }));
        }
        this.setNonEmptyRules();
      },
      deep: true,
    },
  },

  async created() {
    const filterName = 'newTabArchitectureGroupAttributeFilter';
    const newTabArchitectureGroup = this.getSelectedFilter(filterName);
    this.setNonEmptyRules();

    if (!isEmpty(newTabArchitectureGroup)) {
      this.isNewTab = true;
      const key = uniqueId('filter-rule-');
      const rule = {
        ...this.getArchitectureGroupFilterDefaults(),
        attributeValue: [newTabArchitectureGroup],
        newTabRule: true,
      };
      // overwrite default empty filter.
      this.mutableFilterRules = [{ key, rule }];
      // in UI, must pick attribute before picking value. Here we set both in advance, so we need to fetch the data and set nonEmptyRules.
      this.setNonEmptyRules();
      await Promise.all([
        this.updateAttributeFilter({ attributeFilter: rule, fetchData: true }, key),
        // the value from local store is used, so it should be removed now.
        this.resetFilter({ filterName }),
      ]);
      this.isNewTab = false;
    }
  },

  methods: {
    ...mapActions('attributes', ['fetchAvailableAttributeValues']),
    ...mapActions('filters', ['resetFilter']),

    extractRuleMetadata(rules) {
      return rules
        .filter(r => !isEmpty(r.rule))
        .map(r => {
          return pick(r.rule, [
            'attributeKey',
            'attributeFilterType',
            'predicate',
            'attributeValue',
            'displayValue',
            'attributeDescription',
            'displayDescription',
            'newTabRule',
          ]);
        });
    },

    setNonEmptyRules() {
      this.nonEmptyRules = this.extractRuleMetadata(this.mutableFilterRules);
    },

    filterRule() {
      return {
        rule: {},
        key: uniqueId('filter-rule-'),
      };
    },

    addAttributeFilter() {
      this.mutableFilterRules.push(this.filterRule());
    },

    async emitAttributeFilterChange() {
      this.$emit('attributeFilterChange', this.appliedRules);
    },

    async updateRulesWRTAvailableAttributeValue({
      attributeKey = null,
      attributeFilterType = null,
      shouldFetchAvailableAttributes = false,
      attributeDescription = null,
    } = {}) {
      // special case for architecture group filters handled in attribute-filter.vue #formatAndFilterAttributes.
      if (attributeKey !== architectureGroupLevel) {
        forEach(this.mutableFilterRules, ({ rule }) => {
          if (size(rule.attributeValue)) {
            this.$set(
              rule,
              'attributeValue',
              intersection(
                rule.attributeValue,
                map(
                  this.availableAttributeValues[rule.attributeKey],
                  attribute => attribute.attributeKey
                )
              )
            );
          }
        });
      }
      this.setNonEmptyRules();
      if (shouldFetchAvailableAttributes) {
        await this.getAvailableAttributeValues({
          attributeKey,
          attributeFilterType,
          attributeDescription,
        });
      }
    },

    async updateAttributeFilter({ attributeFilter, fetchData }, key) {
      const { attributeKey, attributeFilterType, attributeDescription } = attributeFilter;
      const filterRule = find(this.mutableFilterRules, { key });
      // after changing attribute, if the attribute filter is not currently active, reset its value
      const activeFilterAttributes = this.filterRules.map(attr => attr.attributeKey);
      if (!fetchData && !includes(activeFilterAttributes, attributeFilter.attributeKey)) {
        attributeFilter.attributeValue = [];
      }
      this.$set(filterRule, 'rule', attributeFilter);

      // We should only fetch available values when we select an attribute to filter on.
      // fetchData is set when we have chosen which particular attribute value we want to filter on
      // therefore we already have the available values so we should prevent fetching them again.
      const shouldFetchAvailableAttributes = !fetchData;
      // for new tab created from expanded-product-view, nonEmptyRules has already set in created()
      if (!this.isNewTab) {
        this.updateRulesWRTAvailableAttributeValue({
          shouldFetchAvailableAttributes,
          attributeKey,
          attributeFilterType,
          attributeDescription,
        });
      } else {
        // only run large aggregations and alert queries when autocomplete for filter values is blurred or if a new tab is created from product-reveal
        this.emitAttributeFilterChange();
      }
    },

    async removeAttributeFilter(key) {
      this.mutableFilterRules = filter(this.mutableFilterRules, rule => rule.key !== key);
      this.updateRulesWRTAvailableAttributeValue();
      this.emitAttributeFilterChange();
    },

    // fetch the available attribute values for the newly-selected attribute
    async getAvailableAttributeValues({ attributeKey, attributeFilterType, attributeDescription }) {
      const selectedAttributeMetadata = [
        ...slice(this.nonEmptyRules, 0, -1),
        ...this.fixedFilterRules,
      ];

      return this.fetchAvailableAttributeValues({
        selectedAttributeMetadata,
        selectedAttributeKey: attributeKey,
        selectedAttributeFilterType: attributeFilterType,
        selectedAttributeDescription: attributeDescription,
        selectedAttributeValue: this.filterRule.attributeValue,
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import '@style/base/_variables.scss';

.panel-header {
  background-color: $pricing-filter-bar-color !important;

  &::v-deep .v-label,
  &::v-deep .v-chip__content {
    font-size: 1.2rem;
  }
}

.filter-text {
  color: $pricing-filter-selection-color;
  font-size: 1.25rem;
}

.filter-text-title {
  font-size: 1.25rem;
}
</style>
